<template>
    <PageSidebar>
        <template #sidebarTitle> {{ $t("faq.menu.questions_list") }} </template>
        <template #sidebarCta>
            <div class="flex items-center space-x-2">
                <PageDropdownButtonSm :actions="importActions">
                    <template #button>
                        <ButtonSecondaryIconSm>
                            <DownloadTrayIcon />
                        </ButtonSecondaryIconSm>
                    </template>
                </PageDropdownButtonSm>
                <PageDropdownButtonSm :actions="addActions">
                    <template #button>
                        <ButtonSecondaryIconSm>
                            <AddIcon />
                        </ButtonSecondaryIconSm>
                    </template>
                </PageDropdownButtonSm>
            </div>
        </template>
        <template #sidebarMain>
            <div class="p-6 space-y-5">
                <FormToggleSm
                    :title="$t('faq.layout.show_section_title')"
                    v-model="faq.withSections"
                />
                <template v-if="sections && sections.length">
                    <SectionCard
                        v-for="section in sectionList"
                        :key="section.id"
                        :section="section"
                        @editSection="editSection(section)"
                        @editQuestion="editQuestion"
                        @addQuestion="$emit('addQuestion', section)"
                        @deleteSection="deleteSection(section)"
                    />
                </template>
            </div>
        </template>
        <template #sidebarFooter>
            <cSaveButton @save="saveSnapshot" :disabled="!hasChanges" />
        </template>
    </PageSidebar>
</template>

<script>
import { markRaw } from "vue";
import { mapState, mapMutations, mapActions } from "vuex";
import { PageSidebar, PageDropdownButtonSm } from "@easy/ui/page-components";
import { ButtonSecondaryIconSm } from "@easy/ui/buttons";
import { FolderAddIcon, AddIcon, DownloadTrayIcon, UploadTrayIcon } from "@easy/ui/icons";
import { FormToggleSm } from "@easy/ui/forms";
import SectionCard from "./SectionCard.vue";
import { FAQ_ROUTE_NAMES } from "@/apps/faq/faq.routes";
import { useSaveOrDropModal } from "@/store/useSaveOrDropModal";
import cSaveButton from "@/shared/components/cSaveButton.vue";

export default {
    computed: {
        ...mapState("faq", ["faq", "sections", "hasChanges"]),
        sectionList() {
            return this.sections.filter((s) => !s._deleted);
        },
        addActions() {
            return [
                {
                    icon: markRaw(AddIcon),
                    label: this.$t("faq.questions.add_question"),
                    handler: () => this.$emit("addQuestion"),
                },
                {
                    icon: markRaw(FolderAddIcon),
                    label: this.$t("faq.questions.add_section"),
                    handler: () => this.$emit("addSection"),
                },
            ];
        },
        importActions() {
            return [
                {
                    icon: markRaw(DownloadTrayIcon),
                    label: this.$t("faq.import.import"),
                    handler: () => this.routeTo(FAQ_ROUTE_NAMES.projectImport),
                },
                {
                    icon: markRaw(UploadTrayIcon),
                    label: this.$t("faq.import.export"),
                    handler: () => this.exportQuestions(),
                },
            ];
        },
    },
    components: {
        PageSidebar,
        SectionCard,
        ButtonSecondaryIconSm,
        AddIcon,
        PageDropdownButtonSm,
        DownloadTrayIcon,
        cSaveButton,
        FormToggleSm,
    },
    emits: ["(section)", "addSection", "editSection", "editQuestion", "addQuestion"],
    setup() {
        return useSaveOrDropModal();
    },
    methods: {
        ...mapActions("faq", ["exportQuestions"]),
        ...mapActions("faq/projects", ["loadProject", "saveSnapshot"]),
        ...mapActions("modals", ["showConfirmModal"]),
        ...mapMutations("faq", ["removeSection"]),
        ...mapActions("alert", ["showErrorMessage"]),
        async deleteSection(section) {
            if (this.sectionList.length <= 1) {
                this.showErrorMessage({
                    title: this.$t("faq.questions.cannot_delete_section"),
                    message: this.$t("faq.questions.cannot_delete_section_message"),
                });
                return;
            }
            const decision = await this.showConfirmModal({
                title: this.$t("faq.questions.delete_section_confirm"),
                description: this.$t("faq.questions.delete_section_message"),
                ctaLabel: this.$t("shared.delete"),
            });
            if (decision) {
                this.removeSection(section);
            }
        },
        routeTo(name, forcedView) {
            const { query } = this.$route;
            this.$router.push({ name, query: forcedView ? { ...query, view: forcedView } : query });
        },
        async editSection(section) {
            await this.askToConfirmIfNeeded({
                showModal: this.hasChanges,
                onConfirmChanges: async () => this.saveSnapshot(),
                onDropChanges: async () => this.loadProject(this.faq.uuid),
            });
            this.$emit("editSection", section);
        },
        async editQuestion(question) {
            await this.askToConfirmIfNeeded({
                showModal: this.hasChanges,
                onConfirmChanges: async () => this.saveSnapshot(),
                onDropChanges: async () => this.loadProject(this.faq.uuid),
            });
            this.$emit("editQuestion", question);
        },
    },
};
</script>
